import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, AlertTitle, Box, Button, Chip, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';

/**
 * POM-6598: the informational "potential duplicate prescription" warning. The backend stamps
 * `duplicateOfRxNumber` with the Rx number of another active prescription for the same patient
 * and Product ID; null means no warning. View-only by design (POM-6551): no dismiss, no
 * acknowledgement, and nothing here changes verification or batch verification.
 *
 * Worded as a potential finding, never a confirmed error, and the icon always carries text
 * (tooltip + aria-label) so the warning does not rely on color alone.
 */
export const POTENTIAL_DUPLICATE_TITLE = 'Potential duplicate prescription';
export const POTENTIAL_DUPLICATE_MESSAGE =
  'Other prescriptions for this patient may duplicate this prescription. Review the related prescriptions for details.';

export const potentialDuplicateTooltip = (duplicateOfRxNumber) =>
  `${POTENTIAL_DUPLICATE_TITLE}: may duplicate Rx #${duplicateOfRxNumber}`;

// Shared by the queue icon and the line-item chip: the full alert as a hover/focus popup, with
// the tooltip's own surface removed so only the alert shows.
const alertPopupComponentsProps = {
  tooltip: { sx: { p: 0, bgcolor: 'transparent', maxWidth: 'none' } },
  arrow: { sx: { color: colors.warningAlertIcon } },
};

const alertPopupSx = {
  maxWidth: 'calc(100vw - 32px)',
  '& .potential-duplicate-message': { whiteSpace: { md: 'nowrap' } },
};

// Order Verification queue rows: an icon beside the drug name; hovering shows the full alert.
export const PotentialDuplicateIcon = ({ duplicateOfRxNumber, sx }) => {
  if (!duplicateOfRxNumber) return null;

  const title = potentialDuplicateTooltip(duplicateOfRxNumber);

  return (
    <Tooltip
      arrow
      placement='top'
      enterTouchDelay={0}
      leaveTouchDelay={5000}
      componentsProps={alertPopupComponentsProps}
      title={
        <PotentialDuplicateAlert duplicateOfRxNumber={duplicateOfRxNumber} sx={alertPopupSx} />
      }
    >
      <Box
        component='span'
        role='img'
        aria-label={title}
        tabIndex={0}
        sx={{ display: 'inline-flex', ml: 0.5, verticalAlign: 'middle', ...sx }}
      >
        <WarningAmberIcon sx={{ fontSize: 18, color: colors.warningAlertIcon }} />
      </Box>
    </Tooltip>
  );
};

PotentialDuplicateIcon.propTypes = {
  duplicateOfRxNumber: PropTypes.string,
  sx: PropTypes.shape({}),
};

PotentialDuplicateIcon.defaultProps = {
  duplicateOfRxNumber: null,
  sx: undefined,
};

// Order and prescription details: an inline warning naming the related prescription. With
// onRelatedRxClick the Rx number opens that prescription through the screen's existing dialog.
export const PotentialDuplicateAlert = ({ duplicateOfRxNumber, onRelatedRxClick, sx }) => {
  if (!duplicateOfRxNumber) return null;

  return (
    <Alert
      severity='warning'
      role='status'
      icon={<WarningAmberIcon fontSize='inherit' sx={{ color: colors.warningAlertIcon }} />}
      sx={{
        bgcolor: colors.warningAlertSurface,
        color: colors.warningAlertText,
        border: `1px solid ${colors.warningAlertIcon}`,
        '& .MuiAlert-message': { flex: 1 },
        ...sx,
      }}
    >
      <AlertTitle sx={{ mb: 0.25, fontSize: 14 }}>{POTENTIAL_DUPLICATE_TITLE}</AlertTitle>
      <Box component='span' className='potential-duplicate-message' sx={{ display: 'block' }}>
        {POTENTIAL_DUPLICATE_MESSAGE}
      </Box>
      <Box component='span' sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
        Related prescription:{' '}
        {onRelatedRxClick ? (
          <Button
            variant='text'
            size='small'
            onClick={() => onRelatedRxClick(duplicateOfRxNumber)}
            aria-label={`Open related prescription Rx #${duplicateOfRxNumber}`}
            sx={{
              p: 0,
              minWidth: 0,
              verticalAlign: 'baseline',
              textTransform: 'none',
              fontSize: 'inherit',
              fontWeight: 600,
              color: 'inherit',
              textDecoration: 'underline',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Rx #{duplicateOfRxNumber}
          </Button>
        ) : (
          `Rx #${duplicateOfRxNumber}`
        )}
      </Box>
    </Alert>
  );
};

// Order details and patient profile line items: a compact chip inline after the Rx number. The full
// alert opens on hover or focus, and stays open while the pointer is over it so the related Rx
// link can be clicked. In the popup the message keeps to one line and the related Rx takes the
// second; on narrow screens the message wraps rather than overflowing.
export const PotentialDuplicateChip = ({ duplicateOfRxNumber, onRelatedRxClick }) => {
  if (!duplicateOfRxNumber) return null;

  return (
    <Tooltip
      arrow
      placement='bottom-start'
      enterTouchDelay={0}
      leaveTouchDelay={5000}
      componentsProps={alertPopupComponentsProps}
      title={
        <PotentialDuplicateAlert
          duplicateOfRxNumber={duplicateOfRxNumber}
          onRelatedRxClick={onRelatedRxClick}
          sx={alertPopupSx}
        />
      }
    >
      <Chip
        size='small'
        variant='outlined'
        tabIndex={0}
        aria-label={potentialDuplicateTooltip(duplicateOfRxNumber)}
        icon={<WarningAmberIcon />}
        label='Potential duplicate'
        sx={{
          height: 20,
          px: 0.625,
          gap: 0.375,
          borderRadius: '10px',
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          cursor: 'default',
          color: colors.warningAlertIcon,
          borderColor: colors.warningAlertIcon,
          bgcolor: colors.warningAlertSurface,
          '& .MuiChip-icon': { color: colors.warningAlertIcon, fontSize: 13, m: 0 },
          '& .MuiChip-label': { p: 0 },
          '&:focus-visible': { outline: `2px solid ${colors.warningAlertIcon}`, outlineOffset: 2 },
        }}
      />
    </Tooltip>
  );
};

PotentialDuplicateChip.propTypes = {
  duplicateOfRxNumber: PropTypes.string,
  onRelatedRxClick: PropTypes.func,
};

PotentialDuplicateChip.defaultProps = {
  duplicateOfRxNumber: null,
  onRelatedRxClick: undefined,
};

PotentialDuplicateAlert.propTypes = {
  duplicateOfRxNumber: PropTypes.string,
  onRelatedRxClick: PropTypes.func,
  sx: PropTypes.shape({}),
};

PotentialDuplicateAlert.defaultProps = {
  duplicateOfRxNumber: null,
  onRelatedRxClick: undefined,
  sx: undefined,
};
